import type { AppContext } from '@application/context/AppContext';
import { BackupService, type BackupBundle } from '@infra/backup/BackupService';
import { CoffreFortStorageService } from '@application/persistence/CoffreFortStorageService';
import { CoffreFortFileBridge } from '@infra/persistence/CoffreFortFileBridge';
import { SecureLicenceStore } from '@infra/licence/SecureLicenceStore';
import { isHybridDeployment } from '@infra/licence/DataModeResolver';
import { NetworkStatusService } from '@infra/network/NetworkStatusService';
import { logger } from '@infra/telemetry/Logger';
import { resolveActiveTenantId } from '@application/licence/ActiveTenant';
import { hasHybridConsent } from './HybridConsent';
import { shouldSendHybridCopy } from './HybridVaultPolicy';
import { createEnvelope, bytesToB64 } from './HybridBackupCrypto';
import { HybridBackupConfigService, type HybridSetupState } from './HybridBackupConfigService';
import {
  defaultHybridBackupTransport,
  HybridTransportError,
  type HybridBackupMeta,
  type HybridBackupTransport,
} from './HybridBackupTransport';

const LAST_KEY = (tenantId: string) => `nexxus.hybridVault.${tenantId}.lastSent`;
const STATUS_KEY = (tenantId: string) => `nexxus.hybridVault.${tenantId}.status`;

export interface HybridVaultStatus {
  lastSentAt: string | null;
  lastLiveSyncAt?: string | null;
  lastError: string | null;
  lastCoffreCount: number;
  /** true tant que la phrase secrète de sauvegarde n'est pas configurée : rien n'est envoyé. */
  needsSetup?: boolean;
  setupState?: HybridSetupState;
  /** Date du premier envoi chiffré (les envois antérieurs à cette date étaient en clair). */
  encryptedSince?: string | null;
}

export interface HybridSendOptions {
  /** false = sync au retour réseau, sans e-mail. true = copie du soir. */
  notify?: boolean;
}

export function readHybridVaultStatus(tenantId: string): HybridVaultStatus {
  try {
    const raw = localStorage.getItem(STATUS_KEY(tenantId));
    if (raw) return JSON.parse(raw) as HybridVaultStatus;
  } catch {
    /* ignore */
  }
  const last = localStorage.getItem(LAST_KEY(tenantId));
  return { lastSentAt: last, lastError: null, lastCoffreCount: 0 };
}

function writeStatus(tenantId: string, status: HybridVaultStatus): void {
  localStorage.setItem(STATUS_KEY(tenantId), JSON.stringify(status));
  if (status.lastSentAt) localStorage.setItem(LAST_KEY(tenantId), status.lastSentAt);
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  return bytesToB64(new Uint8Array(buffer));
}

let transport: HybridBackupTransport = defaultHybridBackupTransport;

/** Injection du transport (tests / évolutions du contrat serveur). */
export function setHybridBackupTransport(next: HybridBackupTransport | null): void {
  transport = next ?? defaultHybridBackupTransport;
}

export function getHybridBackupTransport(): HybridBackupTransport {
  return transport;
}

/** Contenu clair LOCAL de la sauvegarde : bundle réimportable tel quel + pièces du coffre-fort. Jamais envoyé tel quel. */
export interface HybridPlainSnapshot extends BackupBundle {
  coffre: Array<{ name: string; relative_path?: string; content_base64: string }>;
}

export class HybridVaultService {
  static isEnabled(ctx: AppContext): boolean {
    return isHybridDeployment(ctx.getLicenceRuntime()?.getState()?.claims ?? null);
  }

  /**
   * Instantané en clair, à chiffrer avant tout envoi. Le manifeste et les données sont exactement
   * ceux de l'export `.bkp` (le hash reste vérifiable par BackupService.verify à la restauration).
   */
  static async collectSnapshot(ctx: AppContext, tenantId: string): Promise<HybridPlainSnapshot> {
    const backup = new BackupService(ctx.db);
    const bundle = await backup.export(tenantId);
    const coffre: HybridPlainSnapshot['coffre'] = [];
    if (CoffreFortFileBridge.isAvailable()) {
      const docs = await CoffreFortStorageService.listDocuments(ctx, tenantId);
      for (const doc of docs) {
        if (!doc.localPath) continue;
        try {
          const bytes = await CoffreFortFileBridge.read(tenantId, doc.localPath);
          coffre.push({
            name: doc.name,
            relative_path: doc.localPath,
            content_base64: arrayBufferToBase64(bytes),
          });
        } catch (err) {
          logger.warn({ event: 'hybrid_vault_coffre_skip', doc: doc.id, error: String(err) });
        }
      }
    }
    return { manifest: bundle.manifest, data: bundle.data, coffre };
  }

  static async sendNow(
    ctx: AppContext,
    tenantId: string,
    options: HybridSendOptions = {},
  ): Promise<HybridVaultStatus> {
    if (!this.isEnabled(ctx)) {
      return { lastSentAt: null, lastError: 'Offre non Hybride', lastCoffreCount: 0 };
    }
    if (!hasHybridConsent(tenantId)) {
      return { lastSentAt: readHybridVaultStatus(tenantId).lastSentAt, lastError: 'Accord Hybride requis', lastCoffreCount: 0 };
    }
    // Le certificat envoyé au serveur est celui de l'entreprise ACTIVE : refuser d'y verser
    // les données d'une autre entreprise du portefeuille.
    const activeTenantId = resolveActiveTenantId(ctx);
    if (activeTenantId && activeTenantId !== tenantId) {
      logger.warn({ event: 'hybrid_vault_tenant_not_active' });
      return {
        lastSentAt: readHybridVaultStatus(tenantId).lastSentAt,
        lastError: 'Entreprise non active — ouvrez ce dossier pour le sauvegarder',
        lastCoffreCount: 0,
      };
    }

    if (!NetworkStatusService.isBrowserOnline()) {
      return { lastSentAt: readHybridVaultStatus(tenantId).lastSentAt, lastError: 'Hors ligne — report au retour réseau', lastCoffreCount: 0 };
    }
    const jwt = await SecureLicenceStore.getActivationCertificate();
    const fingerprint = await SecureLicenceStore.getDeviceFingerprint();
    if (!jwt || !fingerprint) {
      return { lastSentAt: null, lastError: 'Licence locale absente', lastCoffreCount: 0 };
    }
    // Jamais de clair : sans phrase secrète configurée (et clé de récupération confirmée), on n'envoie rien.
    const material = await HybridBackupConfigService.getSendingMaterial();
    if (!material) {
      const snapshot = await HybridBackupConfigService.getSetupSnapshot();
      const previous = readHybridVaultStatus(tenantId);
      const blocked: HybridVaultStatus = {
        ...previous,
        lastError:
          snapshot.state === 'unavailable'
            ? 'Coffre sécurisé du poste indisponible — sauvegarde chiffrée impossible'
            : 'Configuration requise : phrase secrète de sauvegarde',
        needsSetup: true,
        setupState: snapshot.state,
      };
      writeStatus(tenantId, blocked);
      logger.warn({ event: 'hybrid_vault_setup_required', state: snapshot.state });
      await HybridBackupConfigService.announceState();
      return blocked;
    }

    const snapshot = await this.collectSnapshot(ctx, tenantId);
    const envelope = await createEnvelope(JSON.stringify(snapshot), {
      dek: material.dek,
      wrapping: material.wrapping,
      meta: {
        created_at: new Date().toISOString(),
        app_version: BackupService.APP_VERSION,
        schema_version: BackupService.SCHEMA_VERSION,
      },
    });

    let lastError = 'Serveur injoignable';
    try {
      await transport.upload(envelope, { notify: options.notify !== false });
      const now = new Date().toISOString();
      const previous = readHybridVaultStatus(tenantId);
      const status: HybridVaultStatus = {
        lastSentAt: options.notify === false ? previous.lastSentAt : now,
        lastLiveSyncAt: now,
        lastError: null,
        lastCoffreCount: snapshot.coffre.length,
        needsSetup: false,
        setupState: 'ready',
        encryptedSince: previous.encryptedSince ?? now,
      };
      writeStatus(tenantId, status);
      logger.info({
        event: options.notify === false ? 'hybrid_live_sync_sent' : 'hybrid_vault_sent',
        coffre: snapshot.coffre.length,
        encrypted: true,
      });
      return status;
    } catch (err) {
      lastError =
        err instanceof HybridTransportError && err.status
          ? `Envoi refusé (${String(err.status)})`
          : err instanceof Error
            ? err.message
            : String(err);
    }
    const failed = { ...readHybridVaultStatus(tenantId), lastError };
    writeStatus(tenantId, failed);
    return failed;
  }

  static async sendIfDue(ctx: AppContext, tenantId: string): Promise<HybridVaultStatus | null> {
    const current = readHybridVaultStatus(tenantId);
    const last = current.lastSentAt ? new Date(current.lastSentAt) : null;
    if (!shouldSendHybridCopy(last)) return current;
    return this.sendNow(ctx, tenantId);
  }

  /** Métadonnées de la dernière sauvegarde chiffrée côté serveur (null si aucune). */
  static async fetchLatestMeta(): Promise<HybridBackupMeta | null> {
    return transport.fetchLatestMeta();
  }

  /**
   * Vrai si ce poste a déjà envoyé des sauvegardes en CLAIR (ancienne version) et pas encore de chiffrée :
   * l'interface doit alors imposer la configuration de la phrase secrète et prévenir l'administrateur.
   */
  static hasLegacyPlainBackups(tenantId: string): boolean {
    const status = readHybridVaultStatus(tenantId);
    return Boolean(status.lastSentAt) && !status.encryptedSince;
  }
}
